import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './customer-order.reducer';

export const CustomerOrder = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const customerOrderList = useAppSelector(state => state.customerOrder.entities);
  const loading = useAppSelector(state => state.customerOrder.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="customer-order-heading" data-cy="CustomerOrderHeading">
        <Translate contentKey="crawlerBackendApp.customerOrder.home.title">Customer Orders</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.customerOrder.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/customer-order/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.customerOrder.home.createLabel">
              Create new Customer Order
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {customerOrderList && customerOrderList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('orderDate')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.orderDate">Order Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('orderDate')} />
                </th>
                <th className="hand" onClick={sort('desiredDate')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.desiredDate">Desired Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('desiredDate')} />
                </th>
                <th className="hand" onClick={sort('orderStatus')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.orderStatus">Order Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('orderStatus')} />
                </th>
                <th className="hand" onClick={sort('initialFixedAnnualPrice')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.initialFixedAnnualPrice">
                    Initial Fixed Annual Price
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('initialFixedAnnualPrice')} />
                </th>
                <th className="hand" onClick={sort('initialFixedMonthlyPrice')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.initialFixedMonthlyPrice">
                    Initial Fixed Monthly Price
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('initialFixedMonthlyPrice')} />
                </th>
                <th className="hand" onClick={sort('contractType')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.contractType">Contract Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contractType')} />
                </th>
                <th className="hand" onClick={sort('createdTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.createdTimestamp">Created Timestamp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdTimestamp')} />
                </th>
                <th className="hand" onClick={sort('depositRequired')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.depositRequired">Deposit Required</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('depositRequired')} />
                </th>
                <th className="hand" onClick={sort('deposit')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.deposit">Deposit</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('deposit')} />
                </th>
                <th className="hand" onClick={sort('offerFilePath')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.offerFilePath">Offer File Path</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('offerFilePath')} />
                </th>
                <th className="hand" onClick={sort('contractFilePath')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.contractFilePath">
                    Contract File Path
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contractFilePath')} />
                </th>
                <th className="hand" onClick={sort('checkSendEmail')}>
                  <Translate contentKey="crawlerBackendApp.customerOrder.checkSendEmail">Check Send Email</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('checkSendEmail')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.customerOrder.customer">Customer</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.customerOrder.supplier">Supplier</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customerOrderList.map((customerOrder, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/customer-order/${customerOrder.id}`} color="link" size="sm">
                      {customerOrder.id}
                    </Button>
                  </td>
                  <td>
                    {customerOrder.orderDate ? (
                      <TextFormat type="date" value={customerOrder.orderDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {customerOrder.desiredDate ? (
                      <TextFormat type="date" value={customerOrder.desiredDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.OrderStatus.${customerOrder.orderStatus}`} />
                  </td>
                  <td>{customerOrder.initialFixedAnnualPrice}</td>
                  <td>{customerOrder.initialFixedMonthlyPrice}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.ContractType.${customerOrder.contractType}`} />
                  </td>
                  <td>
                    {customerOrder.createdTimestamp ? (
                      <TextFormat type="date" value={customerOrder.createdTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{customerOrder.depositRequired ? 'true' : 'false'}</td>
                  <td>{customerOrder.deposit ? 'true' : 'false'}</td>
                  <td>{customerOrder.offerFilePath}</td>
                  <td>{customerOrder.contractFilePath}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.SendEmailStatus.${customerOrder.checkSendEmail}`} />
                  </td>
                  <td>
                    {customerOrder.customer ? (
                      <Link to={`/customer/${customerOrder.customer.id}`}>{customerOrder.customer.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {customerOrder.supplier ? (
                      <Link to={`/supplier/${customerOrder.supplier.id}`}>{customerOrder.supplier.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/customer-order/${customerOrder.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/customer-order/${customerOrder.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/customer-order/${customerOrder.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.customerOrder.home.notFound">No Customer Orders found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CustomerOrder;
